/**
 * Groups useGameApp flat API into namespaces for GameShell (M6).
 */
import type { useGameApp } from './useGameApp';

export type GameAppReturn = ReturnType<typeof useGameApp>;

export interface GameController {
  navigation: {
    showLanding: GameAppReturn['showLanding'];
    currentPage: GameAppReturn['currentPage'];
    setShowLanding: GameAppReturn['setShowLanding'];
    setCurrentPage: GameAppReturn['setCurrentPage'];
  };
  session: {
    gameStarted: GameAppReturn['gameStarted'];
    gameStatus: GameAppReturn['gameStatus'];
    handleStart: GameAppReturn['handleStart'];
    handleResume: GameAppReturn['handleResume'];
    handleSoftReset: GameAppReturn['handleSoftReset'];
    handleRetryScenario: GameAppReturn['handleRetryScenario'];
    handleExtend: GameAppReturn['handleExtend'];
    handleSaveNow: GameAppReturn['handleSaveNow'];
    hasSave: GameAppReturn['hasSave'];
    savedGameAvailable: GameAppReturn['savedGameAvailable'];
  };
  world: {
    grid: GameAppReturn['grid'];
    stats: GameAppReturn['stats'];
    deltas: GameAppReturn['deltas'];
    simBreakdown: GameAppReturn['simBreakdown'];
    research: GameAppReturn['research'];
    milestones: GameAppReturn['milestones'];
    tickNumber: GameAppReturn['tickNumber'];
    gameSpeed: GameAppReturn['gameSpeed'];
    setGameSpeed: GameAppReturn['setGameSpeed'];
    activeLayer: GameAppReturn['activeLayer'];
    layerVisibility: GameAppReturn['layerVisibility'];
    layerManager: GameAppReturn['layerManager'];
  };
  build: {
    selectedTool: GameAppReturn['selectedTool'];
    setSelectedTool: GameAppReturn['setSelectedTool'];
    selectedTile: GameAppReturn['selectedTile'];
    setSelectedTile: GameAppReturn['setSelectedTile'];
    selectedTileData: GameAppReturn['selectedTileData'];
    handleTileClick: GameAppReturn['handleTileClick'];
    handleUpgrade: GameAppReturn['handleUpgrade'];
    handleDelete: GameAppReturn['handleDelete'];
    getPlacementError: GameAppReturn['getPlacementError'];
    hasJumpGate: GameAppReturn['hasJumpGate'];
  };
  narrative: {
    currentGoal: GameAppReturn['currentGoal'];
    newsFeed: GameAppReturn['newsFeed'];
    isGeneratingGoal: GameAppReturn['isGeneratingGoal'];
    aiEnabled: GameAppReturn['aiEnabled'];
    currentStory: GameAppReturn['currentStory'];
    activeEvent: GameAppReturn['activeEvent'];
    setActiveEvent: GameAppReturn['setActiveEvent'];
    activeScenario: GameAppReturn['activeScenario'];
    scenarioObjectives: GameAppReturn['scenarioObjectives'];
    activeQuiz: GameAppReturn['activeQuiz'];
    setActiveQuiz: GameAppReturn['setActiveQuiz'];
    fetchNewGoal: GameAppReturn['fetchNewGoal'];
    triggerStory: GameAppReturn['triggerStory'];
    handleAskAICoach: GameAppReturn['handleAskAICoach'];
    handleAutoSolve: GameAppReturn['handleAutoSolve'];
    handleClaimReward: GameAppReturn['handleClaimReward'];
  };
  economy: {
    handleTakeLoan: GameAppReturn['handleTakeLoan'];
    handleRepayLoan: GameAppReturn['handleRepayLoan'];
    handlePitchInvestors: GameAppReturn['handlePitchInvestors'];
    handleLaunchProduct: GameAppReturn['handleLaunchProduct'];
    handleStartResearch: GameAppReturn['handleStartResearch'];
  };
  policy: {
    policies: GameAppReturn['policies'];
    policyChallenges: GameAppReturn['policyChallenges'];
    policyMilestones: GameAppReturn['policyMilestones'];
    activePolicy: GameAppReturn['activePolicy'];
    setActivePolicy: GameAppReturn['setActivePolicy'];
    policyEffects: GameAppReturn['policyEffects'];
    handleCreatePolicy: GameAppReturn['handleCreatePolicy'];
    handleSubmitPolicyForReview: GameAppReturn['handleSubmitPolicyForReview'];
    handleApprovePolicy: GameAppReturn['handleApprovePolicy'];
    handleImplementPolicy: GameAppReturn['handleImplementPolicy'];
    handleEnforcePolicy: GameAppReturn['handleEnforcePolicy'];
    handleActivateChallenge: GameAppReturn['handleActivateChallenge'];
    handlePolicyAudit: GameAppReturn['handlePolicyAudit'];
  };
  ui: {
    activeModal: GameAppReturn['activeModal'];
    setActiveModal: GameAppReturn['setActiveModal'];
    showPerformanceMonitor: GameAppReturn['showPerformanceMonitor'];
    togglePerformanceMonitor: GameAppReturn['togglePerformanceMonitor'];
    orgManagementOpen: GameAppReturn['orgManagementOpen'];
    setOrgManagementOpen: GameAppReturn['setOrgManagementOpen'];
    showEnhancedObjectives: GameAppReturn['showEnhancedObjectives'];
    toggleEnhancedObjectives: GameAppReturn['toggleEnhancedObjectives'];
    handleSOCAction: GameAppReturn['handleSOCAction'];
  };
}

export function buildGameController(app: GameAppReturn): GameController {
  return {
    navigation: {
      showLanding: app.showLanding,
      currentPage: app.currentPage,
      setShowLanding: app.setShowLanding,
      setCurrentPage: app.setCurrentPage,
    },
    session: {
      gameStarted: app.gameStarted,
      gameStatus: app.gameStatus,
      handleStart: app.handleStart,
      handleResume: app.handleResume,
      handleSoftReset: app.handleSoftReset,
      handleRetryScenario: app.handleRetryScenario,
      handleExtend: app.handleExtend,
      handleSaveNow: app.handleSaveNow,
      hasSave: app.hasSave,
      savedGameAvailable: app.savedGameAvailable,
    },
    world: {
      grid: app.grid,
      stats: app.stats,
      deltas: app.deltas,
      simBreakdown: app.simBreakdown,
      research: app.research,
      milestones: app.milestones,
      tickNumber: app.tickNumber,
      gameSpeed: app.gameSpeed,
      setGameSpeed: app.setGameSpeed,
      activeLayer: app.activeLayer,
      layerVisibility: app.layerVisibility,
      layerManager: app.layerManager,
    },
    build: {
      selectedTool: app.selectedTool,
      setSelectedTool: app.setSelectedTool,
      selectedTile: app.selectedTile,
      setSelectedTile: app.setSelectedTile,
      selectedTileData: app.selectedTileData,
      handleTileClick: app.handleTileClick,
      handleUpgrade: app.handleUpgrade,
      handleDelete: app.handleDelete,
      getPlacementError: app.getPlacementError,
      hasJumpGate: app.hasJumpGate,
    },
    narrative: {
      currentGoal: app.currentGoal,
      newsFeed: app.newsFeed,
      isGeneratingGoal: app.isGeneratingGoal,
      aiEnabled: app.aiEnabled,
      currentStory: app.currentStory,
      activeEvent: app.activeEvent,
      setActiveEvent: app.setActiveEvent,
      activeScenario: app.activeScenario,
      scenarioObjectives: app.scenarioObjectives,
      activeQuiz: app.activeQuiz,
      setActiveQuiz: app.setActiveQuiz,
      fetchNewGoal: app.fetchNewGoal,
      triggerStory: app.triggerStory,
      handleAskAICoach: app.handleAskAICoach,
      handleAutoSolve: app.handleAutoSolve,
      handleClaimReward: app.handleClaimReward,
    },
    economy: {
      handleTakeLoan: app.handleTakeLoan,
      handleRepayLoan: app.handleRepayLoan,
      handlePitchInvestors: app.handlePitchInvestors,
      handleLaunchProduct: app.handleLaunchProduct,
      handleStartResearch: app.handleStartResearch,
    },
    policy: {
      policies: app.policies,
      policyChallenges: app.policyChallenges,
      policyMilestones: app.policyMilestones,
      activePolicy: app.activePolicy,
      setActivePolicy: app.setActivePolicy,
      policyEffects: app.policyEffects,
      handleCreatePolicy: app.handleCreatePolicy,
      handleSubmitPolicyForReview: app.handleSubmitPolicyForReview,
      handleApprovePolicy: app.handleApprovePolicy,
      handleImplementPolicy: app.handleImplementPolicy,
      handleEnforcePolicy: app.handleEnforcePolicy,
      handleActivateChallenge: app.handleActivateChallenge,
      handlePolicyAudit: app.handlePolicyAudit,
    },
    ui: {
      activeModal: app.activeModal,
      setActiveModal: app.setActiveModal,
      showPerformanceMonitor: app.showPerformanceMonitor,
      togglePerformanceMonitor: app.togglePerformanceMonitor,
      orgManagementOpen: app.orgManagementOpen,
      setOrgManagementOpen: app.setOrgManagementOpen,
      showEnhancedObjectives: app.showEnhancedObjectives,
      toggleEnhancedObjectives: app.toggleEnhancedObjectives,
      handleSOCAction: app.handleSOCAction,
    },
  };
}

